import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AppLayout from '@/layouts/AppLayout';
import { LoadingOverlay } from '@/components/LoadingStates';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useAuth } from '@/state/AuthContext';
import RoleGate from '@/components/RoleGate';
import { appRoutes, publicRoutes } from '@/routes/config';
import { usePrefetchLazyRoutes } from '@/hooks/usePrefetchLazyRoutes';
import './styles/globals.css';

const App: React.FC = () => {
  const { isAuthenticated, status } = useAuth();

  if (status === 'initializing') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-500">
        控制台準備中…
      </div>
    );
  }

  const RouterContent: React.FC<{ isAuthenticated: boolean }> = ({ isAuthenticated }) => {
    const location = useLocation();
    usePrefetchLazyRoutes();
    const wrapWithBoundary = (node: React.ReactNode, key: string) => (
      <ErrorBoundary resetKey={`${key}-${location.key}`}>{node}</ErrorBoundary>
    );
    return (
      <ErrorBoundary resetKey={location.pathname}>
        <React.Suspense fallback={<LoadingOverlay />}>
          <Routes>
          {isAuthenticated ? (
          <>
            <Route element={<AppLayout />}>
              {appRoutes.map((route) => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={<RoleGate allowedRoles={route.roles}>{wrapWithBoundary(route.element, route.path)}</RoleGate>}
                />
              ))}
            </Route>
            {publicRoutes.map((route) => (
              <Route key={route.path} path={route.path} element={<Navigate to="/" replace />} />
            ))}
          </>
        ) : (
          <>
            {publicRoutes.map((route) => (
              <Route key={route.path} path={route.path} element={route.element} />
            ))}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        )}
          </Routes>
        </React.Suspense>
      </ErrorBoundary>
    );
  };

  return (
    <Router>
      <RouterContent isAuthenticated={isAuthenticated} />
    </Router>
  );
};

export default App;
